import { useEffect, useState } from "react";

const STORAGE_KEY = "apx_geo_v1";
// Cache for 7 days — country rarely changes for a user
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Detects the user's country via ipapi.co (free tier, no key required).
 * Cached in localStorage for 7 days so we don't hit the API on every page.
 * Returns:
 *   { country, isIndia, ready }
 *
 * SSR-safe: returns { ready:false } during initial render then resolves.
 */
export function useGeo() {
  const [state, setState] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (cached && Date.now() - cached.t < TTL_MS && cached.country) {
        return {
          country: cached.country,
          isIndia: cached.country === "IN",
          ready: true,
        };
      }
    } catch {}
    return { country: null, isIndia: false, ready: false };
  });

  useEffect(() => {
    if (state.ready) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!r.ok) throw new Error("geo fetch failed");
        const j = await r.json();
        const country = j.country_code || j.country || "US";
        if (cancelled) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ country, t: Date.now() }));
        setState({ country, isIndia: country === "IN", ready: true });
      } catch {
        // On failure, default to non-India (safer for a paid product — shows USD)
        if (!cancelled) setState({ country: "US", isIndia: false, ready: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  return state;
}

// Static helper for non-hook contexts (returns null until useGeo has run once)
export function readCachedCountry() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (cached && Date.now() - cached.t < TTL_MS) return cached.country;
  } catch {}
  return null;
}
